import { useState, type FormEvent } from "react";
import { ExternalLink, RefreshCw, Send, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { AdminNav } from "@/components/admin/AdminNav";
import { track } from "@/lib/analytics";
import { getCitySlug } from "@/lib/city";
import {
  isIdentifierGuardError,
  roundsErrorMessage,
  useAdminRoundsDigestLog,
  useAdminRoundsDigestPreview,
  useAdminRoundsReports,
  useReplyAsAdmin,
  useResolveAdminRoundReport,
  useRoundsStatus,
  useSetRoundsOpen,
  type AdminRoundReport,
  type ModerationResolution,
  type ReportReason,
} from "@/lib/rounds";
import { SEO } from "@/lib/seo";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";
import "@/styles/board.css";
import "@/styles/rounds.css";

const ACTIONS: Array<{ value: ModerationResolution; label: string }> = [
  { value: "noaction", label: "No action" },
  { value: "hide", label: "Hide content" },
  { value: "delete", label: "Delete content" },
  { value: "lock", label: "Lock thread" },
  { value: "ban_community", label: "Ban from community" },
  { value: "ban_forum", label: "Ban from forum" },
];

const REASON_LABELS: Record<ReportReason, string> = {
  patient_identifying: "Patient-identifying information",
  defamation: "Defamation",
  dosing_advice: "Case-specific dosing advice",
  commercial_spam: "Commercial promotion or spam",
  off_topic: "Off topic",
  hate_harassment: "Harassment or hate",
  other: "Other",
};

export default function AdminRounds() {
  const citySlug = getCitySlug();
  const reports = useAdminRoundsReports();
  const status = useRoundsStatus(citySlug);
  const setOpen = useSetRoundsOpen(citySlug);

  return (
    <div className="brand-a pb-admin-invites-page pb-admin-rounds-page">
      <SEO title="Rounds moderation · PulseBoard" description="Moderate Rounds reports and forum access." path="/admin/rounds" noindex />
      <main className="pb-admin-invites-main">
        <AdminNav />
        <header className="pb-admin-heading">
          <div><p>Admin · Clinician cockpit</p><h1>Rounds moderation</h1></div>
          <button
            type="button"
            disabled={reports.isFetching || status.isFetching}
            onClick={() => {
              void reports.refetch();
              void status.refetch();
            }}
          >
            <RefreshCw size={15} aria-hidden="true" />Refresh
          </button>
        </header>

        <section className="pb-admin-rounds-controls" aria-labelledby="rounds-control-heading">
          <div>
            <p className="pb-board-eyebrow">Forum gate</p>
            <h2 id="rounds-control-heading">Rounds open</h2>
            <p>
              {status.data
                ? `${status.data.memberCount} of ${status.data.threshold} members joined. Admins retain access in either state.`
                : "Checking current forum status…"}
            </p>
          </div>
          <label className="pb-admin-rounds-switch">
            <input
              type="checkbox"
              checked={status.data?.open ?? false}
              disabled={!status.data || setOpen.isPending}
              onChange={(event) => setOpen.mutate(event.target.checked)}
            />
            <span aria-hidden="true" />
            {status.data?.open ? "Open" : "Closed"}
          </label>
          {status.isError ? <p className="pb-admin-rounds-error" role="alert">Forum status could not be loaded.</p> : null}
          {setOpen.isError ? <p className="pb-admin-rounds-error" role="alert">The forum gate was not updated.</p> : null}
        </section>

        <section className="pb-admin-rounds-relay" aria-labelledby="rounds-relay-heading">
          <div>
            <p className="pb-board-eyebrow">Specialist relay</p>
            <h2 id="rounds-relay-heading">Reply as admin</h2>
            <p>Relay a response received outside PulseBoard with explicit attribution.</p>
          </div>
          <AdminReplyForm />
        </section>

        <AdminDigestPanel />

        <section className="pb-admin-rounds-queue" aria-labelledby="rounds-reports-heading">
          <div className="pb-admin-section-heading">
            <div>
              <h2 id="rounds-reports-heading">Pending reports</h2>
              <p>Human review within {reports.data?.slaHours ?? 24} hours.</p>
            </div>
            {reports.data ? <span className="pb-admin-membership-total">{reports.data.items.length} awaiting review</span> : null}
          </div>

          {reports.isLoading ? <p className="pb-admin-state">Loading Rounds reports…</p> : null}
          {reports.isError ? <p className="pb-admin-state pb-admin-state--error" role="alert">Rounds reports could not be loaded.</p> : null}
          {reports.data?.items.length === 0 ? <p className="pb-admin-state">No pending Rounds reports.</p> : null}
          <div className="pb-admin-rounds-report-list">
            {reports.data?.items.map((report) => (
              <AdminReportCard key={report.id} report={report} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function AdminDigestPanel() {
  const [userId, setUserId] = useState("");
  const preview = useAdminRoundsDigestPreview();
  const weekStart = previousIsoWeekStart();
  const log = useAdminRoundsDigestLog(weekStart);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const selectedUserId = userId.trim();
    if (!selectedUserId) return;
    preview.reset();
    track("digest_preview");
    preview.mutate(selectedUserId);
  };

  return (
    <section className="pb-admin-rounds-digest" aria-labelledby="rounds-digest-heading">
      <div className="pb-admin-rounds-digest-heading">
        <div>
          <p className="pb-board-eyebrow">Weekly member email</p>
          <h2 id="rounds-digest-heading">Digest</h2>
          <p>Preview a member’s Monday digest and review sends from last week.</p>
        </div>
        <span>{log.data?.eligibleCount ?? "—"} eligible</span>
      </div>

      <div className="pb-admin-rounds-digest-grid">
        <div className="pb-admin-rounds-digest-preview">
          <form onSubmit={submit}>
            <label>
              Member user ID
              <input
                required
                inputMode="text"
                autoComplete="off"
                placeholder="Account user UUID"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              />
            </label>
            <button type="submit" disabled={preview.isPending || !userId.trim()}>
              {preview.isPending ? "Composing…" : "Preview digest"}
            </button>
          </form>

          {preview.isError ? (
            <p className="pb-admin-rounds-error" role="alert">Digest preview could not be loaded.</p>
          ) : null}
          {preview.data && !preview.data.eligible ? (
            <p className="pb-admin-rounds-digest-ineligible" role="status">
              {digestIneligibleMessage(preview.data.reason)}
            </p>
          ) : null}
          {preview.data?.eligible ? (
            <article>
              <header>
                <h3>{preview.data.subject}</h3>
                <span>{preview.data.threadCount} {preview.data.threadCount === 1 ? "thread" : "threads"}</span>
              </header>
              <iframe sandbox="" srcDoc={preview.data.html} title="Digest preview" />
            </article>
          ) : null}
        </div>

        <div className="pb-admin-rounds-digest-log">
          <div>
            <div>
              <h3>Send log</h3>
              <p>Week of {formatDate(weekStart)}</p>
            </div>
            <button type="button" disabled={log.isFetching} onClick={() => void log.refetch()}>
              <RefreshCw size={13} aria-hidden="true" />Refresh
            </button>
          </div>
          {log.isLoading ? <p className="pb-admin-state">Loading digest log…</p> : null}
          {log.isError ? (
            <p className="pb-admin-state pb-admin-state--error" role="alert">
              Digest log could not be loaded. <button type="button" onClick={() => void log.refetch()}>Retry</button>
            </p>
          ) : null}
          {log.data?.items.length === 0 ? <p className="pb-admin-state">No digests were sent.</p> : null}
          {log.data?.items.length ? (
            <div className="pb-admin-table-wrap">
              <table className="pb-admin-table pb-admin-rounds-digest-table">
                <thead><tr><th>Member</th><th>Sent</th><th>Threads</th></tr></thead>
                <tbody>
                  {log.data.items.map((item) => (
                    <tr key={`${item.userId}-${item.sentAt}`}>
                      <td>{item.display}</td>
                      <td><time dateTime={item.sentAt}>{formatDateTime(item.sentAt)}</time></td>
                      <td>{item.threadCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function AdminReportCard({ report }: { report: AdminRoundReport }) {
  const resolve = useResolveAdminRoundReport();
  const [action, setAction] = useState<ModerationResolution>("noaction");
  const [note, setNote] = useState("");
  const tone = report.ageHours < 12 ? "green" : report.ageHours < 24 ? "amber" : "red";

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!note.trim()) return;
    resolve.mutate({ id: report.id, action, note: note.trim() });
  };

  return (
    <article className="pb-admin-rounds-report-card">
      <header>
        <div>
          <span className="pb-admin-rounds-reason"><ShieldAlert size={13} aria-hidden="true" />{REASON_LABELS[report.reason]}</span>
          <h3>{report.target.title || `${report.targetType === "thread" ? "Thread" : "Reply"} report`}</h3>
          <p>{report.target.communitySlug} · {report.target.author.display} (@{report.target.author.handle})</p>
        </div>
        <span className="pb-admin-rounds-sla" data-tone={tone}>
          {formatAge(report.ageHours)} · {tone === "green" ? "within SLA" : tone === "amber" ? "due soon" : "overdue"}
        </span>
      </header>
      <blockquote>{report.target.excerpt || "No excerpt available."}</blockquote>
      {report.note ? <p className="pb-admin-rounds-reporter-note"><strong>Reporter note</strong>{report.note}</p> : null}
      <p className="pb-admin-rounds-reporter">Reported by {report.reporter.display} · <time dateTime={report.createdAt}>{formatDateTime(report.createdAt)}</time></p>
      {report.targetType === "thread" ? (
        <Link className="pb-admin-rounds-open-target" to={`/board/rounds/t/${encodeURIComponent(report.targetId)}`}>
          Open thread<ExternalLink size={12} aria-hidden="true" />
        </Link>
      ) : null}

      {report.targetType === "thread" ? (
        <details className="pb-admin-rounds-inline-reply">
          <summary>Reply as admin on this thread</summary>
          <AdminReplyForm initialThreadId={report.targetId} lockThreadId />
        </details>
      ) : null}

      <form className="pb-admin-rounds-resolution" onSubmit={submit}>
        <label>
          Action
          <select value={action} onChange={(event) => setAction(event.target.value as ModerationResolution)}>
            {ACTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <label>
          Resolution note
          <textarea required maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
        <button type="submit" disabled={resolve.isPending || !note.trim()}>
          {resolve.isPending ? "Resolving…" : "Resolve report"}
        </button>
        {resolve.isError ? <p className="pb-admin-rounds-error" role="alert">The report remains pending. Resolution could not be saved.</p> : null}
      </form>
    </article>
  );
}

function AdminReplyForm({
  initialThreadId = "",
  lockThreadId = false,
}: {
  initialThreadId?: string;
  lockThreadId?: boolean;
}) {
  const reply = useReplyAsAdmin();
  const [threadId, setThreadId] = useState(initialThreadId);
  const [attributedTo, setAttributedTo] = useState("");
  const [body, setBody] = useState("");
  const [sent, setSent] = useState(false);
  const identifierError = isIdentifierGuardError(reply.error);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!threadId.trim() || !attributedTo.trim() || !body.trim()) return;
    reply.mutate(
      {
        threadId: threadId.trim(),
        attributedTo: attributedTo.trim(),
        body: body.trim(),
      },
      {
        onSuccess: (post) => {
          setBody("");
          setSent(true);
          track("post_create", { thread_id: threadId.trim(), post_id: post.id });
        },
      },
    );
  };

  return (
    <form className="pb-admin-rounds-reply-form" onSubmit={submit}>
      <label>
        Thread ID
        <input
          required
          readOnly={lockThreadId}
          value={threadId}
          onChange={(event) => setThreadId(event.target.value)}
        />
      </label>
      <label>
        Attribution
        <input
          required
          maxLength={160}
          placeholder="Dr Chris Hii (by email)"
          value={attributedTo}
          onChange={(event) => setAttributedTo(event.target.value)}
        />
      </label>
      <label className="pb-admin-rounds-reply-body">
        Reply
        <textarea
          required
          maxLength={4000}
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
            setSent(false);
            if (reply.isError) reply.reset();
          }}
        />
        {identifierError ? (
          <span className="pb-rounds-identifier-error" role="alert">
            Remove patient-identifying details before posting. <Link to="/board/rounds/guidelines">Read the case-writing guidelines.</Link>
          </span>
        ) : null}
        <small>{body.length.toLocaleString("en-AU")} / 4,000</small>
      </label>
      <button type="submit" disabled={reply.isPending || !threadId.trim() || !attributedTo.trim() || !body.trim()}>
        <Send size={13} aria-hidden="true" />{reply.isPending ? "Posting…" : "Post attributed reply"}
      </button>
      {sent ? <p className="pb-admin-rounds-success" role="status">Attributed reply posted.</p> : null}
      {!identifierError && reply.isError ? <p className="pb-admin-rounds-error" role="alert">{roundsErrorMessage(reply.error)}</p> : null}
    </form>
  );
}

function formatAge(value: number): string {
  if (value < 1) return `${Math.max(1, Math.round(value * 60))}m`;
  return `${Math.round(value)}h`;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function previousIsoWeekStart(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const localDate = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day)));
  const daysSinceMonday = (localDate.getUTCDay() + 6) % 7;
  localDate.setUTCDate(localDate.getUTCDate() - daysSinceMonday - 7);
  return localDate.toISOString().slice(0, 10);
}

function digestIneligibleMessage(reason: string | null): string {
  switch (reason) {
    case "not_member":
      return "This account is not an eligible Rounds member.";
    case "digest_off":
      return "This member has turned the weekly digest off.";
    case "no_subscriptions":
      return "This member has no digest-enabled community subscriptions.";
    case "nothing_new":
      return "There is nothing new to include for this member.";
    default:
      return "A digest is not available for this member.";
  }
}
